import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Category {
  id: string
  name: string
  icon: string
  article_count: number
}

interface Article {
  id: string
  title: string
  slug: string
  excerpt: string
  views: number
  helpful_yes: number
  helpful_no: number
}

export default function KnowledgeBasePage() {
  const [categories, setCategories] = useState<Category[]>([])
  const [articles, setArticles] = useState<Article[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  useEffect(() => {
    fetchData()
  }, [selectedCategory])

  const fetchData = async () => {
    const catParam = selectedCategory ? `?category_id=${selectedCategory}` : ''
    const [catRes, artRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/knowledge-base/categories'),
      fetch(API_BASE + '/knowledge-base/articles' + catParam),
      fetch(API_BASE + '/knowledge-base/statistics')
    ])
    if (catRes.ok) setCategories((await catRes.json()).categories || [])
    if (artRes.ok) setArticles((await artRes.json()).articles || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const search = async () => {
    if (!searchQuery) return fetchData()
    const res = await fetch(API_BASE + '/knowledge-base/search?query=' + encodeURIComponent(searchQuery))
    if (res.ok) {
      const data = await res.json()
      setArticles(data.results?.filter((r: any) => r.type === 'article').map((r: any) => r.item) || [])
    }
  }

  const getIconEmoji = (icon: string) => {
    switch (icon) {
      case 'rocket': return '🚀'
      case 'chart': return '📊'
      case 'document': return '📄'
      case 'plug': return '🔌'
      case 'wrench': return '🔧'
      default: return '📚'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Knowledge Base</h1>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Articles</div>
              <div className="text-2xl font-bold">{statistics.total_articles}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Categories</div>
              <div className="text-2xl font-bold">{statistics.total_categories}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Views</div>
              <div className="text-2xl font-bold">{statistics.total_views?.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Helpfulness</div>
              <div className="text-2xl font-bold text-green-600">{statistics.helpfulness_rate}%</div>
            </div>
          </div>
        )}

        <div className="mb-6">
          <div className="flex gap-4">
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search()}
              className="flex-1 px-4 py-2 border rounded-lg"
            />
            <button onClick={search} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Search</button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-6">
          <div>
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="font-semibold mb-4">Categories</h2>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full text-left px-3 py-2 rounded ${!selectedCategory ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'}`}
                >
                  All Articles
                </button>
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`w-full text-left px-3 py-2 rounded flex items-center justify-between ${selectedCategory === cat.id ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'}`}
                  >
                    <span>{getIconEmoji(cat.icon)} {cat.name}</span>
                    <span className="text-xs bg-gray-200 px-2 py-1 rounded">{cat.article_count}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="col-span-3">
            <div className="grid grid-cols-2 gap-4">
              {articles.map(article => (
                <div key={article.id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer">
                  <h3 className="font-semibold text-lg mb-2">{article.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{article.excerpt}</p>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>{article.views.toLocaleString()} views</span>
                    <div className="flex gap-2">
                      <span className="text-green-600">👍 {article.helpful_yes}</span>
                      <span className="text-red-600">👎 {article.helpful_no}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
